import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { DisabledCustomerTenderUser } from "~/shared/schemas/auto-dispatch-schema.ts";
import {
	disableCustomerTenderUserRequestSchema,
	disabledCustomerTenderUserSchema,
} from "~/shared/schemas/auto-dispatch-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";

import type { TenderUserTypeId } from "../utils/constants.ts";
import { tenderUserTypeNames } from "../utils/constants.ts";
import { customerDisabledTenderUsersQueryKey } from "./use-get-customer-tender-settings.ts";

export type CustomerTenderSettingChange = {
	tenderUserTypeId: TenderUserTypeId;
	enabled: boolean;
};

const updateCustomerTenderSettings = async (
	customerId: number,
	change: CustomerTenderSettingChange,
	signal?: AbortSignal,
) => {
	const signalOptions = signal ? { signal } : undefined;

	if (change.enabled) {
		const url = apiRoutes.ENABLECUSTOMERTENDERUSER.replace(
			":customerId",
			customerId.toString(),
		).replace(":tenderUserTypeId", change.tenderUserTypeId.toString());
		const resp = await deleteAndValidate(url, z.undefined(), signalOptions);
		if (resp.success || resp.status === 404) {
			return;
		}
		throw new Error(`Failed to enable customer tender type: ${resp.error}`);
	}

	const parsedJson = disableCustomerTenderUserRequestSchema.safeParse({
		customerID: customerId,
		tenderUserTypeID: change.tenderUserTypeId,
	});
	if (!parsedJson.success) {
		throw new Error(`Invalid tender user data: ${parsedJson.error.message}`);
	}

	const resp = await postAndValidate(
		apiRoutes.DISABLECUSTOMERTENDERUSER,
		parsedJson.data,
		disabledCustomerTenderUserSchema,
		signalOptions,
	);
	if (resp.success || resp.status === 409) {
		return;
	}
	throw new Error(`Failed to disable customer tender type: ${resp.error}`);
};

export const useUpdateCustomerTenderSettings = (customerId: number) => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const { mutateAsync: patchCarrier } = useMutation({
		mutationFn: (change: CustomerTenderSettingChange) =>
			updateCustomerTenderSettings(customerId, change),

		onMutate: async (change) => {
			await queryClient.cancelQueries({
				queryKey: customerDisabledTenderUsersQueryKey(customerId),
			});
			const queryKey = customerDisabledTenderUsersQueryKey(customerId);
			const previous =
				queryClient.getQueryData<DisabledCustomerTenderUser[]>(queryKey);

			queryClient.setQueryData(
				queryKey,
				(old: DisabledCustomerTenderUser[] = []) => {
					if (change.enabled) {
						return old.filter(
							(user) => user.tenderUserTypeID !== change.tenderUserTypeId,
						);
					}
					if (
						old.some(
							(user) => user.tenderUserTypeID === change.tenderUserTypeId,
						)
					) {
						return old;
					}
					return [
						...old,
						{
							customerID: customerId,
							tenderUserTypeID: change.tenderUserTypeId,
							tenderUserTypeName: tenderUserTypeNames[change.tenderUserTypeId],
							disabledDateUTC: null,
							disabledByUserId: null,
						},
					];
				},
			);

			return { previous };
		},
		onSuccess: () => {
			showSuccess("Tender Setting Updated");
		},

		onError: (_err, _update, context) => {
			showError(`${_err}`);
			queryClient.setQueryData(
				customerDisabledTenderUsersQueryKey(customerId),
				context?.previous,
			);
		},

		onSettled: () =>
			queryClient.invalidateQueries({
				queryKey: customerDisabledTenderUsersQueryKey(customerId),
			}),
	});

	return { patchCarrier };
};

export const useUpdateCustomerTenderSetting = (customerId: number) => {
	const { patchCarrier } = useUpdateCustomerTenderSettings(customerId);
	return patchCarrier;
};
